import { FormControl } from "baseui/form-control";
import { Check, Delete } from "baseui/icon";
import { Spinner } from "baseui/spinner";
import { Tag } from "baseui/tag";
import React, { useEffect, useState } from "react";
import { BsFileEarmark } from "react-icons/bs";
import styled from "styled-components";
import { FieldGrid } from "../pages/candidate/FieldGrid";
import {
	getDateString,
	getDayPart,
	getMonthPart,
	getRelativeDateString,
	getYearPart,
} from "../utils/date";
import enums from "../utils/enums";
import request from "../utils/http";
import toaster from "../utils/toaster";
import CandidateAddress from "./CandidateAddress";
import CandidateAgreementViewer from "./CandidateAgreementViewer";
import CandidateIdentityDocument from "./CandidateIdentityDocument";
import Copy from "./Copy";
import DateTimeText from "./DateTimeText";
import Markdown from "./Markdown";

interface CandidateDetailProps {
	candidateId: string;
}

const Task = styled.div`
	margin-bottom: 1.5rem;
`;

const Action = styled.div`
	margin-bottom: 1.5rem;
`;

const ActionHeader = styled.div`
	margin-bottom: 0.75rem;
	em {
		font-size: 80%;
	}
`;

const CheckboxContainer = styled.div`
	display: grid;
	grid-template-columns: 3rem auto;
`;

const DateContainer = styled.div`
	display: flex;
	.separator {
		margin-right: 1rem;
	}
`;

const FileContainer = (props: any) => {
	const thisFile = props.file;

	return (
		<div>
			{thisFile.contentType && thisFile.contentType.startsWith("image/") ? (
				<>
					<a
						href={thisFile.privateUrl}
						target="_blank"
						rel="noopener"
						style={{
							textDecoration: "none",
						}}
					>
						<img src={thisFile.privateUrl} />
					</a>
				</>
			) : (
				<>
					<div>
						<a
							href={thisFile.privateUrl}
							target="_blank"
							rel="noopener"
							style={{
								textDecoration: "none",
							}}
						>
							<BsFileEarmark /> <span>Download</span>{" "}
						</a>
					</div>
				</>
			)}
		</div>
	);
};

const CandidateDetail = (props: CandidateDetailProps) => {
	const [data, setData] = useState(null as any);
	const [loaded, setLoaded] = useState(false);

	const [extraData, setExtraData] = useState({} as any);

	const getData = async () => {
		let result = await request(
			"GET",
			`/api/admin/candidate-detail/${props.candidateId}`,
			null,
			true,
			false
		);
		if (result.success && result.data) {
			setData(result.data);
			setLoaded(true);
		}

		await getExtraData();
	};

	const getExtraData = async () => {
		var tmpExtraData = {} as any;

		let employmentAgreementsResult = await request(
			"GET",
			"/api/admin/utility/candidate/employment-agreements",
			null,
			true,
			false
		);
		if (employmentAgreementsResult.success) {
			tmpExtraData["employment-agreement"] =
				employmentAgreementsResult.data.items;
		} else {
			toaster.error("Unable to access employment agreements");
		}

		setExtraData(tmpExtraData);
	};

	useEffect(() => {
		getData();
	}, [props.candidateId]);

	return (
		<>
			{!loaded ? (
				<Spinner />
			) : (
				<div>
					<div>
						{data.taskSets &&
							data.taskSets.map((taskSet: any) => {
								return (
									<div>
										<h2>
											{taskSet.name}{" "}
											{taskSet.completed && (
												<Tag
													closeable={false}
													overrides={{ Text: { style: { maxWidth: "" } } }}
													kind="green"
												>
													Completed
												</Tag>
											)}
											{taskSet.locked && (
												<Tag
													closeable={false}
													overrides={{ Text: { style: { maxWidth: "" } } }}
												>
													Locked
												</Tag>
											)}
											{!taskSet.completed && !taskSet.locked && (
												<Tag
													closeable={false}
													overrides={{ Text: { style: { maxWidth: "" } } }}
													kind="red"
												>
													Incomplete
												</Tag>
											)}
										</h2>
										<Markdown content={taskSet.description} />
										{taskSet.tasks &&
											taskSet.tasks.map((task: any) => {
												return (
													<Task>
														<h3>{task.name}</h3>
														{task.actions &&
															task.actions.map((action: any) => {
																return (
																	<Action>
																		<ActionHeader>
																			<strong>{action.name}</strong> -{" "}
																			<em>
																				{action.completed
																					? "Completed"
																					: "Last modified"}{" "}
																				by {action.lastModifiedBy} on{" "}
																				{getDateString(action.lastModifiedUtc)}{" "}
																				(
																				{getRelativeDateString(
																					action.lastModifiedUtc
																				)}
																				)
																			</em>
																		</ActionHeader>

																		<Markdown content={action.description} />

																		<FieldGrid>
																			{action.fields &&
																				action.fields.map((field: any) => {
																					switch (field.fieldType) {
																						case "Text":
																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										<Copy>
																											{
																												action.initialValues[
																													field.fieldProperties
																														.mapTo
																												]
																											}
																										</Copy>
																									</FormControl>
																								</div>
																							);
																						case "Choose":
																							if (
																								field.fieldProperties.mode ===
																								"items"
																							) {
																								return (
																									<div>
																										<FormControl
																											label={field.name}
																										>
																											<Copy>
																												{extraData &&
																												extraData[
																													field.fieldProperties
																														.optionsFrom
																												]
																													? extraData[
																															field
																																.fieldProperties
																																.optionsFrom
																													  ].find(
																															(item: any) => {
																																return (
																																	item.identifier ==
																																	action
																																		.initialValues[
																																		field
																																			.fieldProperties
																																			.mapTo
																																	]
																																);
																															}
																													  )?.name
																													: "Loading"}
																											</Copy>
																										</FormControl>
																									</div>
																								);
																							} else {
																								return (
																									<div>
																										<FormControl
																											label={field.name}
																										>
																											<Copy>
																												{
																													action.initialValues[
																														field
																															.fieldProperties
																															.mapTo
																													]
																												}
																											</Copy>
																										</FormControl>
																									</div>
																								);
																							}

																						case "Special.ConfirmAndExplain":
																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										<div>
																											{field.fieldProperties
																												?.mode !==
																												"explain-only" && (
																												<div>
																													{action.initialValues[
																														field
																															.fieldProperties
																															.mapCheckBoxTo
																													]
																														? "Yes"
																														: "No"}
																												</div>
																											)}
																											{action.initialValues[
																												field.fieldProperties
																													.mapLinesTo
																											] && (
																												<ul>
																													{action.initialValues[
																														field
																															.fieldProperties
																															.mapLinesTo
																													].map((line: any) => {
																														const lineSplit =
																															line.split("\n");

																														return (
																															<li>
																																{lineSplit.map(
																																	(
																																		lineText: any
																																	) => {
																																		return (
																																			<p>
																																				{
																																					lineText
																																				}
																																			</p>
																																		);
																																	}
																																)}
																															</li>
																														);
																													})}
																												</ul>
																											)}
																										</div>
																									</FormControl>
																								</div>
																							);
																						case "Markdown":
																							return (
																								<Markdown
																									content={
																										field.fieldProperties
																											.content
																									}
																									context={{
																										data: action.initialValues,
																									}}
																								/>
																							);
																						case "Declaration":
																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										<>
																											{field.fieldProperties
																												.declarationText &&
																												field.fieldProperties
																													.declarationText
																													.length > 0 && (
																													<Markdown
																														content={
																															field
																																.fieldProperties
																																.declarationText
																														}
																													/>
																												)}
																											<CheckboxContainer>
																												<div>
																													{action.initialValues[
																														field
																															.fieldProperties
																															.mapTo
																													] ? (
																														<Check size={48} />
																													) : (
																														<Delete size={48} />
																													)}
																												</div>
																												<div>
																													{
																														field
																															.fieldProperties
																															.acceptDeclarationText
																													}
																												</div>
																											</CheckboxContainer>
																										</>
																									</FormControl>
																								</div>
																							);
																						case "Checkbox":
																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										<>
																											<CheckboxContainer>
																												<div>
																													{action.initialValues[
																														field
																															.fieldProperties
																															.mapTo
																													] ? (
																														<Check size={48} />
																													) : (
																														<Delete size={48} />
																													)}
																												</div>
																											</CheckboxContainer>
																										</>
																									</FormControl>
																								</div>
																							);

																						case "DatePicker":
																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										{action.initialValues[
																											field.fieldProperties
																												.mapTo
																										] ? (
																											<DateContainer>
																												<Copy>
																													{getDayPart(
																														action
																															.initialValues[
																															field
																																.fieldProperties
																																.mapTo
																														]
																													)}
																												</Copy>
																												<div className="separator">
																													/
																												</div>
																												<Copy>
																													{getMonthPart(
																														action
																															.initialValues[
																															field
																																.fieldProperties
																																.mapTo
																														]
																													)}
																												</Copy>
																												<div className="separator">
																													/
																												</div>
																												<Copy>
																													{getYearPart(
																														action
																															.initialValues[
																															field
																																.fieldProperties
																																.mapTo
																														]
																													)}
																												</Copy>
																											</DateContainer>
																										) : (
																											<>Not provided</>
																										)}
																									</FormControl>
																								</div>
																							);

																						case "FileUpload":
																							const thisFile =
																								action.initialValues[
																									field.fieldProperties.mapTo
																								];

																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										{thisFile ? (
																											<FileContainer
																												file={thisFile}
																											/>
																										) : (
																											<>No file uploaded</>
																										)}
																									</FormControl>
																								</div>
																							);
																						case "Special.IdentityDocuments":
																							return (
																								<>
																									<CandidateIdentityDocument
																										fields={
																											action.initialValues
																										}
																										mode="detail"
																									/>
																								</>
																							);
																						case "Select":
																							let selectValue = "";

																							switch (
																								field.fieldProperties
																									.optionsFromEnumType
																							) {
																								case "stringList":
																									selectValue =
																										action.initialValues[
																											field.fieldProperties
																												.mapTo
																										];
																									break;
																								case "object":
																										selectValue = (enums as any)[
																											field.fieldProperties.optionsFromEnum.toString()
																										].find((enumItem: any) => {
																											return (
																												enumItem.value ==
																												action.initialValues[
																													field.fieldProperties
																														.mapTo
																												]
																											);
																										})?.label;
																									break;

																								default:
																									selectValue =
																										JSON.stringify(
																											(enums as any)[
																												field.fieldProperties.optionsFromEnum.toString()
																											]
																										) +
																										" " +
																										field.fieldProperties
																											.optionsFromEnumType;
																									break;
																							}

																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										<>
																											<Copy>{selectValue}</Copy>
																										</>
																									</FormControl>
																								</div>
																							);

																						case "Address":
																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										<CandidateAddress
																											address={
																												action.initialValues[
																													field.fieldProperties
																														.mapTo
																												]
																											}
																										/>
																									</FormControl>
																								</div>
																							);

																						case "ManyAddresses":
																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										<ul>
																											{action.initialValues[
																												field.fieldProperties
																													.mapTo
																											] &&
																												action.initialValues[
																													field.fieldProperties
																														.mapTo
																												].map(
																													(address: string) => {
																														return (
																															<li>
																																<Copy>
																																	{address}
																																</Copy>
																															</li>
																														);
																													}
																												)}
																										</ul>
																									</FormControl>
																								</div>
																							);

																						case "Agreement":
																							return (
																								<div>
																									<FormControl
																										label={field.name}
																									>
																										<>
																											Acknowledged and signed by Candidate?
																											<CheckboxContainer>
																												<div>
																													{action.initialValues[
																														`${field.fieldProperties.mapTo}Completed`
																													] ? (
																														<Check size={48} />
																													) : (
																														<Delete size={48} />
																													)}
																												</div>
																											</CheckboxContainer>
																											{action.initialValues[
																												`${field.fieldProperties.mapTo}Completed`
																											] && (
																												<>
																													Accepted{" "}
																													<DateTimeText
																														utcDateTime={
																															action
																																.initialValues[
																																`${field.fieldProperties.mapTo}CompletedDate`
																															]
																														}
																													/>
																												</>
																											)}
																										</>
																									</FormControl>
																								</div>
																							);

																						default:
																							return (
																								<code>
																									{JSON.stringify(field)}
																								</code>
																							);
																					}
																				})}
																		</FieldGrid>
																	</Action>
																);
															})}
													</Task>
												);
											})}
										<hr />
									</div>
								);
							})}
					</div>
				</div>
			)}
		</>
	);
};
export default CandidateDetail;
